<script setup lang="ts">
import type { NoviceView } from '~/features/my-wallet/types/receiving-model'
import { useI18n } from 'vue-i18n'
import IconInfo from '~icons/material-symbols/info-outline'
import MilestoneStepper from '~/features/my-wallet/components/charts/MilestoneStepper.vue'

interface Props {
  novice: NoviceView
}

defineProps<Props>()

const { t } = useI18n()
</script>

<template>
  <div class="novice-progress">
    <div class="novice-progress-stats">
      <div class="novice-progress-stat">
        <p class="novice-progress-label">
          {{ t('my_wallet.receiving_model.novice.progress_label') }}
        </p>
        <p class="novice-progress-value">
          {{ t('my_wallet.receiving_model.novice.progress_value', { completed: novice.completed, total: novice.required }) }}
        </p>
      </div>
      <div class="novice-progress-stat">
        <p class="novice-progress-label">
          {{ novice.isDone
            ? t('my_wallet.receiving_model.novice.challenge_label_done')
            : t('my_wallet.receiving_model.novice.challenge_label') }}
        </p>
        <p class="novice-progress-value">
          {{ novice.isDone
            ? t('my_wallet.receiving_model.novice.challenge_value_done')
            : t('my_wallet.receiving_model.novice.challenge_value', { total: novice.required }) }}
        </p>
      </div>
    </div>

    <MilestoneStepper
      size="large"
      :completed="novice.completed"
      :total="novice.required"
      :label="t('my_wallet.receiving_model.novice.stepper_aria', { completed: novice.completed, total: novice.required })"
    />

    <div class="novice-progress-alert">
      <IconInfo class="novice-progress-alert-icon" aria-hidden="true" />
      <div class="novice-progress-alert-text">
        <p class="novice-progress-alert-title">
          {{ t('my_wallet.receiving_model.novice.alert_title') }}
        </p>
        <p>{{ t('my_wallet.receiving_model.novice.alert_description') }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/typography';

.novice-progress {
  @apply flex flex-col gap-6 w-full p-6 rounded-lg;
  @apply bg-neutral-high-pure border border-neutral-high-medium;

  .novice-progress-stats {
    @apply flex flex-col gap-6;

    @screen sm:system {
      @apply flex-row flex-wrap;
      column-gap: 5rem;
    }
  }

  .novice-progress-stat {
    @apply flex flex-col;
    gap: 10px;
  }

  .novice-progress-label {
    @apply uppercase font-bold text-brand-primary-medium;
    font-size: 0.625rem;
    line-height: 1.5;
  }

  .novice-progress-value {
    @apply text-neutral-low-dark;
    @include typography.title-20;
  }

  .novice-progress-alert {
    @apply flex items-center gap-4 p-4 rounded-md;
    @apply bg-feedback-informative-light border border-feedback-informative-medium text-feedback-informative-dark;
  }

  .novice-progress-alert-icon {
    @apply w-6 h-6 flex-shrink-0 text-feedback-informative-pure;
  }

  .novice-progress-alert-text {
    @apply flex flex-col gap-1 fonts-body-medium-regular;
  }

  .novice-progress-alert-title {
    @apply fonts-body-large-bold;
  }
}
</style>
